import { HttpHeaders } from '@angular/common/http';
import { DestroyRef, inject, Inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  OtpChallengeMetadata,
  OtpChallengeResult,
  OtpCreateChallengeOptions,
  OtpCreateChallengeRequest,
  OtpDeliveryChannel,
  OtpHeaderResponse,
  OtpResendChallengeOptions,
  OtpVerifyChallengeRequest,
  OtpVerifyChallengeResult,
} from '@app/models/otp.model';
import { CommonUtilityService } from '@app/services/common-utility.service';
import { DalService } from '@upr-web-primer/dal';
import { NotificationService } from '@upr-web-primer/notification';
import { LocalStorageService } from '@upr-web-primer/storage';
import { ApplicationDataService } from './application-data.service';
import { UdxSpinnerService } from '@upr-web-primer/udx-spinner';
import { isEmpty } from '@upr-web-primer/udx-utils';
import { environment } from '@env/environment';
import { CookieService } from 'ngx-cookie-service';

const OTP_CHALLENGES_PATH = '/oidc/api/otp-challenges';
const CHALLENGE_ID_TOKEN = '{challenge-id}';
const OTP_CHALLENGE_PATH = `${OTP_CHALLENGES_PATH}/${CHALLENGE_ID_TOKEN}`;
const OTP_VERIFY_PATH = `${OTP_CHALLENGE_PATH}/verify`;
const OTP_RESEND_PATH = `${OTP_CHALLENGE_PATH}/resend`;
const DEFAULT_SOURCE_KEY = 'WEB_ACCOUNTCREATION';
@Injectable({ providedIn: 'root' })
export class OtpService {
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly dal: DalService,
    private readonly common: CommonUtilityService,
    private readonly localStorage: LocalStorageService,
    private readonly notification: NotificationService,
    @Inject('AppDefaults') private readonly appDefaults: any,
    @Inject('sourceKeys') private readonly sourceKeys: any,
    private readonly applicationDataService: ApplicationDataService,
    private readonly spinner: UdxSpinnerService,
    @Inject('AppRoutes') private readonly appRoutes: any,
    public cookieService: CookieService,
  ) {}

  /**
   * Creates a new OTP challenge by sending a POST request to the OTP challenges endpoint.
   *
   * Hides any active notification banner first so the guest can proceed to step 2
   * without a stale error blocking the view, then delegates the HTTP call to the DAL
   * and normalizes the response into an {@link OtpChallengeResult}.
   *
   * @param request - Challenge creation payload (delivery channel, identifier, etc.).
   * @param options - Optional headers/config such as `sourceId`, `idempotencyKey`, and `xsrfToken`.
   * @returns A promise resolving to the created challenge result (challengeId, masked destination, etc.).
   * @throws Re-throws the underlying HTTP error after invoking {@link handleHttpError} for user-facing feedback.
   */
  async createChallenge(
    request: OtpCreateChallengeRequest,
    options: OtpCreateChallengeOptions = {},
  ): Promise<OtpChallengeResult> {
    try {
      const response = await this.dal.httpRequest(
        OTP_CHALLENGES_PATH,
        'POST',
        request,
        await this.buildHeaders(true, options),
        undefined,
        undefined,
        true,
      );

      // Only hide the banner on success — the caller is transitioning to
      // step 2 and any stale error banner should be cleared. On failure,
      // handleHttpError() surfaces a fresh banner and we must not hide it.
      this.notification.hide();
      return this.toChallengeResult(response);
    } catch (error) {
      this.handleCreateChallengeHttpError(error);
      throw error;
    }
  }

  async getChallenge(challengeId: string): Promise<OtpChallengeMetadata> {
    try {
      const response = await this.dal.httpRequest(
        this.withChallengeId(OTP_CHALLENGE_PATH, challengeId),
        'GET',
        {},
        await this.buildHeaders(true, {}),
      );

      return this.toChallengeMetadata(challengeId, response);
    } catch (error) {
      this.handleGetChallengeHttpError(error);
      throw error;
    }
  }

  async verifyChallenge(
    challengeId: string,
    request: OtpVerifyChallengeRequest,
  ): Promise<OtpVerifyChallengeResult> {
    try {
      const response = await this.dal.httpRequest(
        this.withChallengeId(OTP_VERIFY_PATH, challengeId),
        'POST',
        request,
        await this.buildHeaders(true, {}),
      );

      return this.toVerifyResult(response);
    } catch (error) {
      this.handleVerifyHttpError(error);
      throw error;
    }
  }

  async resendChallenge(
    challengeId: string,
    options: OtpResendChallengeOptions = {},
  ): Promise<OtpChallengeResult> {
    try {
      const response = await this.dal.httpRequest(
        this.withChallengeId(OTP_RESEND_PATH, challengeId),
        'POST',
        {},
        await this.buildHeaders(true, options),
        undefined,
        undefined,
        true,
      );
      return this.toChallengeResult(response, challengeId);
    } catch (error) {
      this.handleResendHttpError(error);
      throw error;
    }
  }

  /**
   * Centralized error handler for HTTP/OTP calls that surfaces a localized banner to the guest.
   *
   * Resolves the error message from the current page labels (falling back to a generic
   * message) and shows a notification when the failure is either an HTTP error with
   * status >= 400 or a non-HTTP failure (network, timeout, parse error, etc.).
   *
   * HTTP 429 clears any existing notification instead of showing a generic error
   * because callers route directly to the dedicated limit-reached view.
   *
   * @param error - The error thrown by the DAL/HTTP layer; may or may not include a numeric `status`.
   */
  private handleHttpError(error: any): void {
    if (error?.status === 429) {
      this.notification.hide();
      return;
    }

    const pageLabels = this.common.getResolvedPageData();
    const errorMessage =
      pageLabels?.['errCode.E019'] ||
      'There was a problem. Please try again later.';

    const hasHttpStatus = typeof error?.status === 'number';

    const isInformationalOrRedirect =
      hasHttpStatus && error.status > 0 && error.status < 400;

    if (isInformationalOrRedirect) {
      return;
    }

    this.notification.show(errorMessage);
    this.common.triggerSystemErrorDDLEvent(errorMessage);
  }

  /**
   * Error handler specifically for `verifyChallenge` errors.
   *
   * HTTP 404 and 422 are handled inline by step 2. All other failures use the
   * generic notification behavior; HTTP 429 routes to the limit-reached view.
   *
   * @param error - The error thrown by the DAL/HTTP layer.
   */
  private handleVerifyHttpError(error: any): void {
    if (error?.status === 404 || error?.status === 422) {
      // 404 → expired code; 422 → invalid code. Step 2 owns both inline errors.
      return;
    }

    // HTTP 429 is handled centrally by handleHttpError, so no local guard is needed.
    this.handleHttpError(error);
  }

  /**
   * Handles `getChallenge` errors consistently with challenge creation.
   * HTTP 429 clears stale notifications before step 1 emits `limitReached`;
   * all other failures use the generic notification behavior.
   *
   * @param error - The error thrown by the DAL/HTTP layer.
   */
  private handleGetChallengeHttpError(error: any): void {
    // HTTP 429 is handled centrally by handleHttpError, so no local guard is needed.
    this.handleHttpError(error);
  }

  /**
   * Error handler for `resendChallenge` errors.
   *
   * HTTP 409 is handled inline by step 2 as an incorrect-code style OTP input
   * error. HTTP 429 on resend is suppressed by the base handler — the caller
   * differentiates between max-resends-reached (navigate to limit-reached)
   * and cooldown-not-elapsed (show toast). HTTP 404 means the challenge has
   * expired, so step 2 creates a replacement challenge without showing a
   * transient error banner. All other failures surface the generic banner.
   *
   * @param error - The error thrown by the DAL/HTTP layer.
   */
  private handleResendHttpError(error: any): void {
    if (error?.status === 404 || error?.status === 409) {
      return;
    }
    this.handleHttpError(error);
  }

  /** Handles create errors through the centralized notification behavior. */
  private handleCreateChallengeHttpError(error: any): void {
    // HTTP 429 is handled centrally by handleHttpError, so no local guard is needed.
    this.handleHttpError(error);
  }

  private async buildHeaders(
    includeResortAreaCode: boolean,
    options: OtpCreateChallengeOptions | OtpResendChallengeOptions,
  ): Promise<HttpHeaders> {
    const accessToken = await this.getAccessToken();
    let headers = new HttpHeaders()
      .append('Authorization', `Bearer ${accessToken}`)
      .append('x-source-id', options.sourceId || this.getDefaultSourceId());

    if (includeResortAreaCode) {
      headers = headers.append(
        'x-resort-area-code',
        this.appDefaults?.RESORT_AREA_CODE ?? '',
      );
    }

    if ('idempotencyKey' in options && options.idempotencyKey) {
      headers = headers.append('Idempotency-Key', options.idempotencyKey);
    }

    if (options.xsrfToken) {
      headers = headers.append('X-XSRF-TOKEN', options.xsrfToken);
    }
    options.observe = 'response';
    return headers;
  }

  private getDefaultSourceId(): string {
    const sourceKey =
      this.sourceKeys?.[DEFAULT_SOURCE_KEY] ?? DEFAULT_SOURCE_KEY;
    return this.common.getSourceId(sourceKey);
  }

  private async getAccessToken(): Promise<string> {
    const accessToken =
      this.localStorage.getItem('OIDC_ACCESS_TOKEN') ||
      this.localStorage.getItem('oAuthToken') ||
      '';

    if (!accessToken) {
      throw new Error('OIDC access token is not available for OTP requests');
    }

    return accessToken;
  }

  public toChallengeResult(
    response: unknown,
    fallbackChallengeId = '',
  ): OtpChallengeResult {
    const headerResponse = response as OtpHeaderResponse<
      Record<string, unknown>
    >;
    const body = this.getBody(response);
    const location = this.getHeader(headerResponse, 'Location');
    const headerChallengeId = this.getHeader(headerResponse, 'X-Location-Id');
    const challengeId =
      headerChallengeId ||
      this.readString(body, 'challenge_id') ||
      this.readString(body, 'challengeId') ||
      this.readString(body, 'id') ||
      this.getChallengeIdFromLocation(location) ||
      fallbackChallengeId;

    return {
      challengeId,
      location: location || undefined,
      guestId: this.readString(body, 'guest_id') || undefined,
      status: this.readString(body, 'status') as OtpChallengeResult['status'],
      channel: this.readString(body, 'channel') as OtpDeliveryChannel,
      destination:
        this.readString(body, 'destination') ||
        this.readString(body, 'destination_masked') ||
        this.readString(body, 'masked_destination') ||
        undefined,
      expiresAt:
        this.readString(body, 'expires_at') ||
        this.readString(body, 'expiresAt') ||
        undefined,
      resendAvailableAt:
        this.readString(body, 'next_resend_available_at') ||
        this.readString(body, 'resendAvailableAt') ||
        undefined,
      retryAfterSeconds: this.readNumber(body, 'retry_after_seconds'),
      resendsRemaining:
        this.readNumber(body, 'resends_remaining') ??
        this.readNumber(body, 'resendsRemaining'),
      raw: response,
    };
  }

  private toChallengeMetadata(
    fallbackChallengeId: string,
    response: unknown,
  ): OtpChallengeMetadata {
    const body = this.getBody(response);

    return {
      challengeId:
        this.readString(body, 'challenge_id') ||
        this.readString(body, 'challengeId') ||
        this.readString(body, 'id') ||
        fallbackChallengeId,
      guestId: this.readString(body, 'guest_id') || undefined,
      status: this.readString(body, 'status') as OtpChallengeMetadata['status'],
      channel: this.readString(body, 'channel') as OtpDeliveryChannel,
      destination:
        this.readString(body, 'destination') ||
        this.readString(body, 'destination_masked') ||
        this.readString(body, 'masked_destination') ||
        undefined,
      expiresAt:
        this.readString(body, 'expires_at') ||
        this.readString(body, 'expiresAt') ||
        undefined,
      resendAvailableAt:
        this.readString(body, 'next_resend_available_at') ||
        this.readString(body, 'resendAvailableAt') ||
        undefined,
      attemptsRemaining:
        this.readNumber(body, 'attempts_remaining') ??
        this.readNumber(body, 'attemptsRemaining'),
      resendsRemaining:
        this.readNumber(body, 'resends_remaining') ??
        this.readNumber(body, 'resendsRemaining'),
      createdAt:
        this.readString(body, 'created_at') ||
        this.readString(body, 'createdAt') ||
        undefined,
      raw: response,
    };
  }

  private toVerifyResult(response: unknown): OtpVerifyChallengeResult {
    const body = this.getBody(response);

    return {
      verified:
        this.readBoolean(body, 'verified') ??
        this.readString(body, 'status').toUpperCase() === 'VERIFIED',
      nextAction:
        this.readString(body, 'next_action') ||
        this.readString(body, 'nextAction') ||
        undefined,
      redirectUrl:
        this.readString(body, 'redirect_url') ||
        this.readString(body, 'redirectUrl') ||
        undefined,
      raw: response,
    };
  }

  private withChallengeId(path: string, challengeId: string): string {
    return path.replace(CHALLENGE_ID_TOKEN, encodeURIComponent(challengeId));
  }

  private getBody(response: unknown): Record<string, unknown> {
    if (!response || typeof response !== 'object') {
      return {};
    }

    const maybeBody = (response as OtpHeaderResponse).body;

    if (maybeBody && typeof maybeBody === 'object') {
      return maybeBody as Record<string, unknown>;
    }

    return response as Record<string, unknown>;
  }

  private getHeader(
    response: OtpHeaderResponse<Record<string, unknown>>,
    name: string,
  ): string {
    return (
      response.headers?.get(name) ||
      response.headers?.get(name.toLowerCase()) ||
      ''
    );
  }

  private getChallengeIdFromLocation(location: string): string {
    if (!location) {
      return '';
    }

    const path = location.split('?')[0];
    return path.substring(path.lastIndexOf('/') + 1);
  }

  private readString(source: Record<string, unknown>, key: string): string {
    const value = source[key];
    return typeof value === 'string' ? value : '';
  }

  private readNumber(
    source: Record<string, unknown>,
    key: string,
  ): number | undefined {
    const value = source[key];

    if (typeof value === 'number') {
      return value;
    }

    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value);
      return Number.isNaN(parsed) ? undefined : parsed;
    }

    return undefined;
  }

  private readBoolean(
    source: Record<string, unknown>,
    key: string,
  ): boolean | undefined {
    const value = source[key];
    return typeof value === 'boolean' ? value : undefined;
  }

  /**
   * Authenticates the user for commerce operations by obtaining B2C OAuth token
   * and performing post-auth login. On completion (success or failure), redirects to WTS.
   *
   *
   * SECURITY ACKNOWLEDGMENT:
   * This method stores B2C OAuth tokens ('authObj', 'loginAuthToken', 'commerceTokenInfo')
   * in localStorage. This is an accepted architectural decision due to application-wide
   * storage requirements where sessionStorage cannot be used.
   *
   * Justification: The overall application architecture requires localStorage for token
   * persistence across tabs and browser sessions for store functionality.
   *
   * Mitigations in place:
   * - Tokens are cleared on explicit logout
   * - Application implements CSP headers to mitigate XSS risks
   * - Token validation occurs on each API request
   */
  public commerceAuthentication(): void {
    this.spinner.show();
    this.common
      .getB2COAuthToken()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (authData: any) => {
          this.localStorage.setItem('authObj', JSON.stringify(authData));
          if (authData) {
            this.common
              .postauthLogin()
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe({
                next: (apiResponse: any) => {
                  this.spinner.hide();

                  this.localStorage.setItem(
                    'loginAuthToken',
                    JSON.stringify(apiResponse),
                  );

                  this.applicationDataService.setApplicationCookieInfo(
                    'commerceTokenInfo',
                    JSON.stringify(apiResponse),
                  );
                  this.common.onClickRedirectWTS('', '', true, false);
                },
                error: () => {
                  this.spinner.hide();
                  this.clearCommerceAuthTokens();
                  this.common.onClickRedirectWTS('', '', true, false);
                },
              });
          } else {
            // Handle falsy authData: hide spinner to prevent permanent loading state
            this.spinner.hide();
            this.clearCommerceAuthTokens();
            this.common.onClickRedirectWTS('', '', true, false);
          }
        },
        error: () => {
          this.spinner.hide();
          this.clearCommerceAuthTokens();
          this.common.onClickRedirectWTS('', '', true, false);
        },
      });
  }

  /**
   * Clears all commerce authentication tokens from sessionStorage.
   * Should be called on logout or authentication failure to prevent token leakage.
   */
  public clearCommerceAuthTokens(): void {
    this.localStorage.removeItem('authObj');
    this.localStorage.removeItem('loginAuthToken');
    this.applicationDataService.deleteCookie('commerceTokenInfo');
  }

  getNavigationUrls(): string {
    const hotelNavigation = this.cookieService.get('Hotel');
    const isHotelNavigation = !isEmpty(hotelNavigation);
    const navigationURL = isHotelNavigation
      ? environment.htlHostBaseUrl
      : environment.wtsHostBaseUrl;
    const isWtsCartCreateAccountValue =
      this.applicationDataService.getCookieInfo('cartCreateAccount') === 'true'
        ? true
        : false;
    const targetUrl = isWtsCartCreateAccountValue
      ? navigationURL
      : this.appRoutes.CREATE_ACCOUNT;
    return targetUrl;
  }
}
